import { useEffect, useRef, useState } from 'react';
import { decode } from 'blurhash';
import { cn } from '@/lib/utils';

interface MediaImageProps {
  /** Full resolved URL (run resolveMediaUrl() before passing here) */
  src: string | null | undefined;
  /** Blurhash string from media.blurhash — shown as shimmer while image loads */
  blurhash?: string | null;
  alt: string;
  className?: string;
  /** Fallback shown when src is null and no blurhash — defaults to indigo gradient */
  fallback?: React.ReactNode;
  /**
   * How the image should fit inside the container.
   * - 'cover' (default): scale to fill, crop to aspect ratio (classic card thumbnail)
   * - 'contain': scale to fit entirely, letterbox if aspect ratios differ (shows the
   *   whole image regardless of source orientation — good for mixed-aspect course
   *   thumbnails coming from a CMS where the curator-picked dimensions should be
   *   preserved)
   */
  fit?: 'cover' | 'contain';
}

/**
 * Image component with blurhash shimmer.
 *
 * Lifecycle:
 *   1. If `blurhash` provided → decode to canvas, show as placeholder
 *   2. Load `<img>` in background
 *   3. On load → fade image in over canvas
 *   4. On error OR both null → show gradient fallback
 *
 * If `blurhash` is null (e.g. course thumbnail not from media table) the
 * component renders a simple `<img>` with a gradient placeholder — same as
 * before, no regression.
 */
/** How many times a failed image is re-requested before the fallback is shown for good. */
const MAX_IMAGE_ATTEMPTS = 2;

/** Backoff between attempts. Short — this is a picture, not a job. */
const RETRY_DELAYS_MS = [600, 1800];

export function MediaImage({
  src,
  blurhash,
  alt,
  className,
  fallback,
  fit = 'cover',
}: MediaImageProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  /**
   * Bumped to retry a failed image. Appended to the URL as a cache-busting parameter, because
   * a browser caches a failed response and a plain re-render would re-use the failure.
   */
  const [attempt, setAttempt] = useState(0);
  const retryTimer = useRef<number | null>(null);

  // Draw blurhash onto canvas when it changes
  useEffect(() => {
    if (!blurhash || !canvasRef.current) return;
    setImageLoaded(false);
    setImageError(false);

    try {
      const canvas = canvasRef.current;
      const W = 32;
      const H = 32;
      canvas.width = W;
      canvas.height = H;

      const pixels = decode(blurhash, W, H); // Uint8ClampedArray RGBA
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const imageData = ctx.createImageData(W, H);
      imageData.data.set(pixels);
      ctx.putImageData(imageData, 0, 0);
    } catch {
      // Invalid blurhash — ignore, fallback handles it
    }
  }, [blurhash]);

  // Reset state when src changes
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
    setAttempt(0);
  }, [src]);

  /**
   * RETRY A FAILED IMAGE.
   *
   * Until 24 Sep a single failed request was permanent: `onError` set the flag, the card
   * dropped to its gradient fallback, and nothing ever tried again — the picture stayed gone
   * until a full page reload. That is invisible on a good connection and constant on a bad
   * one, and it was reported on iPhone during a Cloudflare APAC degradation, where whole grids
   * settled into fallbacks.
   *
   * Two attempts with a short backoff. An image that fails three times is failing for a reason
   * a fourth request will not fix — a deleted object, a wrong path — and retrying forever would
   * turn one broken card into a steady stream of requests.
   */
  useEffect(() => {
    if (!imageError || !src || attempt >= MAX_IMAGE_ATTEMPTS) return;

    retryTimer.current = window.setTimeout(() => {
      setImageError(false);
      setAttempt((a) => a + 1);
    }, RETRY_DELAYS_MS[attempt] ?? 2000);

    return () => {
      if (retryTimer.current !== null) {
        window.clearTimeout(retryTimer.current);
        retryTimer.current = null;
      }
    };
  }, [imageError, src, attempt]);

  // The fallback appears only when there is nothing left to try. While retries remain the
  // blurhash stays up, so a transient failure looks like slow loading rather than a broken
  // card — which is what it is.
  const retriesExhausted = imageError && attempt >= MAX_IMAGE_ATTEMPTS;
  const showFallback = (!src && !blurhash) || retriesExhausted;

  if (showFallback) {
    return (
      <div className={cn('bg-gradient-to-br from-primary/80 to-primary/40 flex items-center justify-center', className)}>
        {fallback}
      </div>
    );
  }

  return (
    <div className={cn('relative overflow-hidden', className)}>
      {/* Blurhash canvas — visible while image loads */}
      {blurhash && !imageLoaded && (
        <canvas
          ref={canvasRef}
          aria-hidden="true"
          className="absolute inset-0 w-full h-full"
          style={{ imageRendering: 'auto' }}
        />
      )}

      {/* Gradient placeholder when no blurhash but src is present */}
      {!blurhash && !imageLoaded && src && (
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-secondary animate-pulse"
        />
      )}

      {/* Actual image */}
      {src && (
        <img
          // The parameter only appears on a retry, so a first load is a normal cacheable
          // request and an unchanged URL keeps its cache entry.
          src={attempt === 0 ? src : `${src}${src.includes('?') ? '&' : '?'}retry=${attempt}`}
          alt={alt}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
          className={cn(
            'w-full h-full transition-opacity duration-300',
            fit === 'contain' ? 'object-contain' : 'object-cover',
            imageLoaded ? 'opacity-100' : 'opacity-0',
          )}
        />
      )}
    </div>
  );
}
